import { inject, Injectable, signal } from '@angular/core';
import { Observable, map, of, tap } from 'rxjs';
import { BuilderService, ContentData, PreviewResult } from '@galaxy/email-ui/builder-v3';
import { createEmail$, getMultiEmails$, renderEmail$, updateEmail$ } from '@vendasta/protos/emailbuilder/v1/emails';
import { EmailCategory, OwnerType } from '@vendasta/protos/emailbuilder/v1/enums';
import { AppNamespace, TemplatesService } from '@galaxy/email-ui/email-builder-sdk';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  mapContentDataToProtoContent,
  mapContentDataToProtoEmailSettings,
  mapProtoEmailToContentData,
  mapTemplateToContentData,
  serializeToEmailJson,
} from '../../email-v2/services/email-builder-content-adapters';
import { DEFAULT_CONTENT } from '../../email-v2/services/builder-dependencies.factory';

export interface AutomationEmailBuilderDialogData {
  emailId?: string;
  templateId?: string;
  ownerType?: OwnerType;
  ownerId?: string;
  namespace: string;
  parentNamespace: string;
}

@Injectable()
export class AutomationEmailBuilderService implements BuilderService {
  private readonly dialogData: AutomationEmailBuilderDialogData = inject(MAT_DIALOG_DATA);
  private readonly templatesService = inject(TemplatesService);

  readonly saved = signal<{ emailId: string } | null>(null);

  private emailId: string | undefined = this.dialogData.emailId;

  getBuilderContent(): Observable<ContentData> {
    const existingEmailId = this.dialogData.emailId;
    if (existingEmailId) {
      return getMultiEmails$({
        namespace: this.dialogData.namespace,
        emailBuilderEmailIds: [existingEmailId],
        parentNamespace: this.dialogData.parentNamespace,
      }).pipe(map((response) => mapProtoEmailToContentData(response.emails?.[0])));
    }

    if (this.dialogData.templateId) {
      return this.templatesService
        .getTemplate(
          this.dialogData.ownerType ?? OwnerType.OWNER_TYPE_SYSTEM,
          this.dialogData.ownerId ?? '',
          AppNamespace.EMAIL_LIBRARY,
          this.dialogData.templateId,
        )
        .pipe(map((template) => mapTemplateToContentData(template)));
    }

    return of(DEFAULT_CONTENT as ContentData);
  }

  saveContent(data: ContentData): Observable<void> {
    const emailContent = {
      displayName: data.name ?? '',
      emailSubject: data.subjectLine ?? '',
      emailJson: serializeToEmailJson(data),
    };

    const existingEmailId = this.emailId;
    const request$ = existingEmailId
      ? updateEmail$({
          namespace: this.dialogData.namespace,
          emailBuilderEmailId: existingEmailId,
          emailContent,
          parentNamespace: this.dialogData.parentNamespace,
        }).pipe(map(() => existingEmailId))
      : createEmail$({
          namespace: this.dialogData.namespace,
          content: emailContent,
          parentTemplateId: this.dialogData.templateId ?? '',
          parentNamespace: this.dialogData.parentNamespace,
          emailCategory: EmailCategory.EMAIL_CATEGORY_MARKETING,
        }).pipe(map((response) => response.emailBuilderEmailId));

    return request$.pipe(
      tap((emailId) => {
        this.emailId = emailId;
        this.saved.set({ emailId });
      }),
      map(() => void 0),
    );
  }

  generatePreview(contentData: ContentData): Observable<PreviewResult> {
    return renderEmail$({
      namespace: this.dialogData.namespace,
      parentNamespace: this.dialogData.parentNamespace,
      content: mapContentDataToProtoContent(contentData),
      emailSettings: mapContentDataToProtoEmailSettings(contentData),
      hydrationOptions: { contactId: 'CONTACT', renderFakeData: true },
    }).pipe(
      map((response) => ({
        html: response.renderedHtml,
        subject: response.subject ?? '',
        previewText: response.previewText ?? '',
      })),
    );
  }
}
